import { useEffect, useContext } from 'react';
import { useLazyFetchSectionResultQuery } from '../service';
import { AnalysisInProgressContext } from '../ContextWrapper';
import pLimit from 'p-limit';

export default function useLiveSectionFetching() {
    const {
        lastAnalysisGuid,
        lastAnalysisUrl,
        lastAnalysisSections,
        lastAnalysisCompetitors,
        setSectionResults,
        setCompetitorSectionResults,
        setIsAnalysisInProgress,
    } = useContext(AnalysisInProgressContext);

    const [fetchSectionResult] = useLazyFetchSectionResultQuery();

    useEffect(() => {
        if (!lastAnalysisGuid || !lastAnalysisUrl || !lastAnalysisSections.length) return;

        setIsAnalysisInProgress(true);

        const competitors = lastAnalysisCompetitors || [];
        const limit = pLimit(5 + competitors.length);

        let cancelled = false;

        async function fetchAllSections() {
            let sectionRequests: Promise<void>[] = [];

            lastAnalysisSections.forEach((section) => {
                sectionRequests.push(limit(async () => {
                    try {
                        if (lastAnalysisGuid) {
                            const response = await fetchSectionResult({ guid: lastAnalysisGuid, section }).unwrap();
                            if (!cancelled) {
                                setSectionResults((last) => ({ ...last, [section]: { ...response, passRate: response.top_100_pass_rate } }));
                            }
                        }
                    } catch {
                        if (!cancelled) {
                            setSectionResults((last) => ({ ...last, [section]: null }));
                        }
                    }
                }));

                competitors.forEach((competitor) => {
                    sectionRequests.push(limit(async () => {
                        try {
                            const response = await fetchSectionResult({ guid: competitor.reportGuid, section }).unwrap();
                            if (!cancelled) {
                                setCompetitorSectionResults((last) => ({
                                    ...last,
                                    [section]: { ...last[section], [competitor.websiteUrl]: response.result }
                                }));
                            }
                        } catch {
                            if (!cancelled) {
                                setCompetitorSectionResults((last) => ({
                                    ...last,
                                    [section]: { ...last[section], [competitor.websiteUrl]: null }
                                }));
                            }
                        }
                    }));
                });
            });

            await Promise.all(sectionRequests);
            if (!cancelled) setIsAnalysisInProgress(false);
        }

        fetchAllSections();

        return () => { cancelled = true; };
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [lastAnalysisGuid, lastAnalysisUrl, lastAnalysisSections, lastAnalysisCompetitors]);
}